import { useMemo } from "react";
import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { format, subDays, subMonths, parseISO } from "date-fns";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";

import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { PaidMediaApiFactory } from "@/apis/brand-os/paid-media";
import type { TrendSeriesItem } from "@/schema/brand-os/paid-media";

type ChartDataPoint = { label: string; spend: number; cac: number };

function toChartData(series: TrendSeriesItem[]): ChartDataPoint[] {
  return series.map((item) => ({
    label: format(parseISO(item.period_start), "MMM d"),
    spend: item.spend ?? 0,
    cac: item.cac ?? 0,
  }));
}

const chartConfig = {
  spend: { label: "Spend", color: "hsl(221 83% 53%)" },
  cac: { label: "CAC", color: "hsl(160 84% 39%)" },
} satisfies ChartConfig;

function formatCurrencyShort(value: number): string {
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
  return `₹${value.toLocaleString("en-IN")}`;
}

function TrendChart({ data }: { data: ChartDataPoint[] }) {
  const maxSpend = Math.max(...data.map((d) => d.spend));
  const spendDomainMax = Math.ceil(maxSpend / 10000) * 10000 || 10000;

  const maxCac = Math.max(...data.map((d) => d.cac));
  const cacDomainMax = Math.ceil(maxCac / 10) * 10 || 100;

  return (
    <ChartContainer config={chartConfig} className="w-full h-72">
      <AreaChart accessibilityLayer data={data} margin={{ top: 8, right: 32, bottom: 8, left: 8 }}>
        <defs>
          <linearGradient id="paid-media-spend-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-spend)" stopOpacity={0.15} />
            <stop offset="100%" stopColor="var(--color-spend)" stopOpacity={0.02} />
          </linearGradient>
          <linearGradient id="paid-media-cac-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--color-cac)" stopOpacity={0.15} />
            <stop offset="100%" stopColor="var(--color-cac)" stopOpacity={0.02} />
          </linearGradient>
        </defs>

        <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/50" />

        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} interval={3} />

        <YAxis
          yAxisId="spend"
          orientation="left"
          domain={[0, spendDomainMax]}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
          tickFormatter={(v: number) => formatCurrencyShort(v)}
          label={{ value: "Spend →", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "hsl(221 83% 53%)", fontWeight: 500, textAnchor: "middle" } }}
        />

        <YAxis
          yAxisId="cac"
          orientation="right"
          domain={[0, cacDomainMax]}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tick={{ fill: "hsl(160 84% 39%)", fontSize: 12 }}
          tickFormatter={(v: number) => `₹${v}`}
          label={{ value: "← CAC", angle: 90, position: "insideRight", style: { fontSize: 11, fill: "hsl(160 84% 39%)", fontWeight: 500, textAnchor: "middle" } }}
        />

        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              formatter={(value, name) => (
                <span className="text-muted-foreground">
                  {name === "spend" ? "Spend" : "CAC"}: <span className="text-foreground font-medium">{typeof value === "number" ? formatCurrencyShort(value) : "N/A"}</span>
                </span>
              )}
            />
          }
        />

        <Area yAxisId="spend" dataKey="spend" type="monotone" stroke="var(--color-spend)" strokeWidth={2} fill="url(#paid-media-spend-fill)" dot={false} />
        <Area yAxisId="cac" dataKey="cac" type="monotone" stroke="var(--color-cac)" strokeWidth={2} fill="url(#paid-media-cac-fill)" dot={false} />
      </AreaChart>
    </ChartContainer>
  );
}

function ChartContent({ data }: { data: ChartDataPoint[] }) {
  const allZero = data.every((d) => d.spend === 0 && d.cac === 0);
  return !data.length || allZero ? (
    <LockedContent message="Trend data will appear once campaigns have activity" />
  ) : (
    <TrendChart data={data} />
  );
}

function TrendsChartContent({ status, data }: { status: QueryStatus; data: ChartDataPoint[] }) {
  switch (status) {
    case "pending":
      return <TrendChartSkeleton />;
    case "error":
      return <LockedContent message="Trend data will appear once campaigns have activity" />;
    case "success":
      return <ChartContent data={data} />;
  }
}

export function TrendsChart() {
  const params = useMemo(() => {
    const yesterday = subDays(new Date(), 1);
    return {
      date_range_start: format(subMonths(yesterday, 1), "yyyy-MM-dd"),
      date_range_end: format(yesterday, "yyyy-MM-dd"),
    };
  }, []);

  const { data, status } = useQuery(PaidMediaApiFactory.Queries.FetchTrends(params));
  const chartData = useMemo(() => toChartData(data?.series ?? []), [data]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold">This Month</CardTitle>
        <CardAction>
          <div className="flex items-center gap-4">
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="size-2.5 rounded-full" style={{ background: "hsl(221 83% 53%)" }} />
              Spend(₹L)
            </p>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="size-2.5 rounded-full" style={{ background: "hsl(160 84% 39%)" }} />
              CAC(₹)
            </p>
          </div>
        </CardAction>
      </CardHeader>
      <CardContent>
        <TrendsChartContent status={status} data={chartData} />
      </CardContent>
    </Card>
  );
}

function TrendChartSkeleton() {
  return (
    <div className="w-full h-72 flex flex-col justify-between">
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-3 w-full rounded" />
        ))}
      </div>
      <div className="relative mt-6 h-full">
        <Skeleton className="absolute bottom-10 left-0 w-full h-1 rounded-full" />
        <Skeleton className="absolute bottom-20 left-0 w-full h-1 rounded-full opacity-60" />
      </div>
    </div>
  );
}
